import type { CrmFieldInstances, TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { CrmRowGroupRole } from 'components/editor/grid/reduxStore/table.types';
import { isGeneratedRow } from 'components/editor/grid/reduxStore/crmRowMarkers';

export type AuthoredGroupPair = { parentRow: TableRowType; childRow: TableRowType };

export function findAuthoredGroupPairs(rows: readonly TableRowType[]): AuthoredGroupPair[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const pairs: AuthoredGroupPair[] = [];

  rows.forEach((row) => {
    if (row.crmRowKind !== undefined) return;
    if (row.crmRowGroupRole !== CrmRowGroupRole.PARENT) return;
    if (row.crmGroupPairedRowId === undefined) return;

    const childRow = byId.get(row.crmGroupPairedRowId);
    if (!childRow) return;
    if (childRow.crmRowKind !== undefined) return;
    if (childRow.crmRowGroupRole !== CrmRowGroupRole.CHILD) return;
    if (childRow.crmGroupPairedRowId !== row.id) return;

    pairs.push({ parentRow: row, childRow });
  });

  return pairs;
}

// 'unbound'    — row was never CRM-bound; it cannot orphan anything by itself
// 'references' — row still points at a CRM array variable
// 'orphaned'   — row was CRM-bound and just lost its last reference
export type CrmRowRefState = 'unbound' | 'references' | 'orphaned';

// Family rule: a Group pair (parent + first child) is orphaned only when NEITHER authored
// row still references a CRM array. A row still referencing one protects the whole family,
// regardless of which row's cell actually changed. Rows outside any pair keep today's flat,
// per-row rule.
export function resolveOrphanedSourceRowIds(rows: readonly TableRowType[], classify: (row: TableRowType) => CrmRowRefState): Set<string> {
  const orphanedSourceRowIds = new Set<string>();
  const pairedRowIds = new Set<string>();

  findAuthoredGroupPairs(rows).forEach(({ parentRow, childRow }) => {
    pairedRowIds.add(parentRow.id);
    pairedRowIds.add(childRow.id);

    const parentState = classify(parentRow);
    const childState = classify(childRow);

    if (parentState === 'references' || childState === 'references') return;
    if (parentState === 'orphaned' || childState === 'orphaned') {
      orphanedSourceRowIds.add(parentRow.id);
      orphanedSourceRowIds.add(childRow.id);
    }
  });

  rows.forEach((row) => {
    if (pairedRowIds.has(row.id)) return;
    if (classify(row) === 'orphaned') orphanedSourceRowIds.add(row.id);
  });

  return orphanedSourceRowIds;
}

// Never removes an authored row — callers that delete authored rows do so themselves before
// calling in here. Returns the input by reference when nothing is orphaned:
// pruneOrphanedCrmRowsAcrossSections (crmCountSync.ts) checks `prunedRows === contentTable.rows`
// to decide whether a table update needs to be emitted at all.
export function dropGeneratedRowsForSources(rows: TableRowType[], orphanedSourceRowIds: ReadonlySet<string>): TableRowType[] {
  if (orphanedSourceRowIds.size === 0) return rows;

  return rows.filter((row) => !(isGeneratedRow(row) && row.crmSourceRowId !== undefined && orphanedSourceRowIds.has(row.crmSourceRowId)));
}

export function applyColumnRemovalToCrmFieldInstances(
  row: TableRowType,
  columnField: string
): { row: TableRowType; state: CrmRowRefState } {
  if (isGeneratedRow(row) || !row.crmFieldInstances) return { row, state: 'unbound' };

  const remainingEntries = Object.entries(row.crmFieldInstances).filter(([key]) => !key.startsWith(`${columnField}:`));
  const nextCrmFieldInstances: CrmFieldInstances | undefined =
    remainingEntries.length > 0 ? Object.fromEntries(remainingEntries) : undefined;

  return {
    row: { ...row, crmFieldInstances: nextCrmFieldInstances },
    state: remainingEntries.length > 0 ? 'references' : 'orphaned',
  };
}
